import {
  calculateTimeAgo,
  getBestTimeDisplay,
  getTotalSeconds,
  TimeAgo,
} from "../hooks/useTimeAgo";
import { PlanViewStateEntry } from "../store/realTimeUpdates";
import labels from "./labels";
import {
  abortedStatus,
  completedStatus,
  failedStatus,
  missingStatus,
  missingWithStuckJobStatus,
  outdatedStatus,
  PondooStatusBarObject,
  runningStatus,
} from "./statusBarStateObjects";

export const getProgressBarValue = (
  timeAgo: TimeAgo | null
): number | undefined => {
  if (!timeAgo) return;

  const totalSeconds = getTotalSeconds(timeAgo);

  const anticipatedFinishTimeMinutes = totalSeconds / 60 < 2 ? 2 : 6;
  const anticipatedFinishTimeSeconds = anticipatedFinishTimeMinutes * 60;

  return Math.min(
    100,
    Math.max(0, (totalSeconds / anticipatedFinishTimeSeconds) * 100)
  );
};

export const getStatusLabel = (timeAgo?: TimeAgo) => {
  return timeAgo
    ? labels.pondooCompleted(getCompletedTimeAgoLabel(timeAgo))
    : labels.pondooStatusCalculating;
};

const getCompletedTimeAgoLabel = (timeAgo: TimeAgo): string => {
  const timeDisplay = getBestTimeDisplay(timeAgo);
  switch (timeDisplay.unit) {
    case "days":
      return labels.dayAmount(timeDisplay.value);
    case "hours":
      return labels.hoursAmount(timeDisplay.value);
    case "minutes":
      return labels.minutesAmount(timeDisplay.value);
    case "seconds":
      return labels.secondsAmount(timeDisplay.value);
  }
};

export const getTabLabel = (
  status: string,
  completedTimeAgo: TimeAgo | null
): string => {
  switch (status) {
    case "ready":
      return getStatusLabel(completedTimeAgo ?? undefined);
    case "loading":
      return labels.pondooRunning;
    case "error":
      return labels.pondooStatusError;
    case "missing":
      return labels.pondooMissing;
    default:
      return "Unknown Status";
  }
};

// Sets a max-width to allow for smooth unfold animation
export const getMaxWidth = (pondooStatusBarObject: PondooStatusBarObject) => {
  return pondooStatusBarObject.status === "Running" ||
    pondooStatusBarObject.status === "NotScheduled" ||
    pondooStatusBarObject.status === "Scheduled" ||
    pondooStatusBarObject.buttonTitle
    ? "500px"
    : "250px";
};

export const determinePondooStatusBarState = (
  viewState: PlanViewStateEntry,
  timeAgo: TimeAgo | null | undefined,
  isOutdated: boolean
): PondooStatusBarObject => {
  const { status } = viewState;

  const isReady = status === "ready";
  const isLoading = status === "loading";
  const isError = status === "error";
  const isMissing = status === "missing";

  if (isMissing) {
    if (!!viewState.job) return missingWithStuckJobStatus;
    return missingStatus;
  }

  if (isLoading) {
    return runningStatus;
  }
  if (isReady) {
    if (isOutdated) {
      return outdatedStatus;
    }

    timeAgo = !!timeAgo
      ? timeAgo
      : calculateTimeAgo(viewState.job?.completedDateTime);
    return completedStatus(timeAgo!);
  }
  if (isError) {
    if (viewState.job?.jobStatus === "Aborted") return abortedStatus;
    return failedStatus;
  }

  // Default case
  return missingStatus;
};
